import { useQuery } from '@apollo/client';
import type {
  ColumnFiltersState,
  PaginationState,
} from '@tanstack/react-table';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';

import addIcon from '../../assets/icons/add.svg';
import smallSettings from '../../assets/icons/smallSettings.svg';
import AddButton from '../../components/ui/common/AddButton';
import Breadcrumb from '../../components/ui/common/Breadcrumb';
import DataTable from '../../components/ui/common/DataTable/DataTable';
import DTHeader from '../../components/ui/common/DataTable/DTHeader';
import { CLIENTS_TABLES_MAX_RESULTS } from '../../config/constants';
import { useModal } from '../../hooks/useModal';
import { locationSelectedToEdit } from '../../recoil/dataToEdit';
import { locationFilter } from '../../recoil/locationsFilters';
import { needRefreshState } from '../../recoil/needRefresh';
import { GET_LOCATIONS } from '../../services/graphql/locations/locationsQuery';
import type { TLocation } from '../../types/locations';

const Locations = () => {
  const { t } = useTranslation('common');
  const [filters, setFilters] = useRecoilState(locationFilter);
  const { handleOpenNewLocationModal } = useModal();
  const setLocation = useSetRecoilState(locationSelectedToEdit);

  // Sorting
  const handleChangeSortDirection = (columnName: any) => {
    setFilters((prev) => ({
      ...prev,
      orderDirection: prev?.orderDirection === 'DESC' ? 'ASC' : 'DESC',
      orderBy: columnName,
    }));
  };
  // const notifyUpdateSuccess = () => toast.success(t('settings.notifySuccess'));
  const [data, setData] = useState<Array<TLocation>>([]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: CLIENTS_TABLES_MAX_RESULTS,
  });
  const [total, setTotal] = useState<number>(0);

  const columnHelper = createColumnHelper<TLocation>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        size: 150,
        minSize: 150,
        maxSize: 200,
        cell: (info) => (
          <span className="text-xs font-bold">{info.getValue()}</span>
        ),
        header: ({ header, ...info }) => (
          <DTHeader
            sortable
            onClick={() => handleChangeSortDirection(header.id)}
            title={t('locations.name')}
            sorting={(info as any).orderBy}
            sortingDirection={(info as any).orderDirection}
            accessor="name"
          />
        ),
      }),
      columnHelper.accessor('address', {
        size: 150,
        minSize: 150,
        maxSize: 200,
        cell: (info) => (
          <span className="text-xs font-medium">{info.getValue()}</span>
        ),

        header: ({ header, ...info }) => (
          <DTHeader
            sortable
            onClick={() => handleChangeSortDirection(header.id)}
            title={t('locations.address')}
            sorting={(info as any).orderBy}
            sortingDirection={(info as any).orderDirection}
            accessor="address"
          />
        ),
      }),
      columnHelper.accessor('measuring_method', {
        size: 150,
        minSize: 150,
        maxSize: 200,
        header: ({ header, ...info }) => (
          <DTHeader
            sortable
            onClick={() => handleChangeSortDirection(header.id)}
            title={t('locations.measuring_method')}
            sorting={(info as any).orderBy}
            sortingDirection={(info as any).orderDirection}
            accessor="measuring_method"
          />
        ),
        cell: (info) => {
          return (
            <span className="text-xs font-medium !capitalize">
              {info.getValue().charAt(0).toUpperCase() +
                info.getValue().slice(1)}
            </span>
          );
        },
      }),
    ],
    [],
  );

  const {
    data: fetchData,
    loading,
    error,
    refetch: refetchLocations,
  } = useQuery(GET_LOCATIONS, {
    variables: {
      limit: pageSize,
      offset: pageIndex * pageSize,
      orderBy: filters?.orderBy || 'name',
      orderDirection: filters?.orderDirection || 'DESC',
    },
  });

  useEffect(() => {
    if (fetchData && fetchData.getLocations && fetchData.getLocations.data) {
      const updatedData: TLocation[] = fetchData.getLocations.data.map(
        (location: TLocation) => ({
          id: location.id,
          name: location.name || '',
          address: location.address || '',
          measuring_method: location.measuring_method || '',
        }),
      );
      setData(updatedData);
      setTotal(fetchData.getLocations.count);
    }
  }, [fetchData]);

  const [needRefresh] = useRecoilState(needRefreshState);

  useEffect(() => {
    refetchLocations({
      limit: pageSize,
      offset: pageIndex * pageSize,
      ...filters,
    });
  }, [filters, needRefresh, pageIndex, pageSize]);

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(total / pageSize),
    state: {
      pagination,
      columnFilters,
    },
    onPaginationChange: (newPagination) => setPagination(newPagination),
  });

  const handleRowClick = (id: string, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const isCellClick = target.classList.contains('cell-class');
    const locationSelected = data.find((d) => d.id === id);
    if (isCellClick && locationSelected) {
      setLocation(locationSelected);
      handleOpenNewLocationModal();
    }
  };

  return (
    <>
      <section>
        <div className="fixed top-0 z-20 w-full bg-white">
          <Breadcrumb
            title={`${t('settings.title')}`}
            secondTitle={`${t('settings.location')}`}
            image={smallSettings}
          />
        </div>
        <div className="mt-8 flex flex-col">
          <div className="flex justify-between">
            <h1 className="flex font-inter text-xl font-bold">
              {t('settings.location')}
            </h1>
            <AddButton
              text={t('locations.addButton')}
              icon={addIcon}
              id="newLocationButton"
              handleAction={handleOpenNewLocationModal}
            />
          </div>
          <div className="mt-5">
            <DataTable
              error={error}
              loading={loading}
              table={table}
              total={total}
              tableId="LocationTable"
              sorting={{ ...filters }}
              loadingRows={total || pageSize}
              handleRowClick={handleRowClick}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Locations;
